<template>
  <div class="lockers">
    <Loading v-if="loading" />
    <div class="lockers__header">
      <div class="lockers__header__inputs">
        <Dropdown
          v-if="activePickupBox"
          class="lockers__header__inputs-dropdown"
          :selected-option="selectedPickupBox"
          :options="pickupBoxOptions"
          data-testid="dropdownLockers"
          @select-option="changePickupBox"
        />
        <div v-if="!activePickupBox" class="lockers__header-notPickupBox">
          <p>{{ $t('noPickupBox') }}</p>
        </div>
        <div
          :class="{
            selectMultipleLockers: true,
            tootip: !activePickupBox?.info.active
          }"
        >
          <Toggle
            v-if="false"
            :title="$t('selectMultipleLockers')"
            :value="multipleLockers"
            name="multipleLockers"
            :disabled="!activePickupBox?.info.active"
            @change="multipleLockers = !multipleLockers"
          />
          <span v-if="!activePickupBox?.info.active" class="tooltiptext">
            {{ $t('pickupboxIsInactive') }}
          </span>
        </div>
        <div
          :class="{
            lockers__header__quickReservation: true,
            tootip: !activePickupBox?.info.active || disableButton
          }"
        >
          <Button
            :class="{
              'lockers__header__quickReservation-button': true,
              'lockers__header__quickReservation-disabled':
                !activePickupBox?.info.active
            }"
            :variant="getVariant()"
            :title="$t('quickReservationLockers')"
            :disabled="!activePickupBox?.info.active || disableButton"
            @click="quickReservation"
          />
          <span v-if="!activePickupBox?.info.active" class="tooltiptext">
            {{ $t('pickupboxIsInactive') }}
          </span>
          <span v-if="disableButton" class="tooltiptext">
            {{ $t('noSpaceInPickupBoxes') }}
          </span>
        </div>
      </div>
      <div class="lockers__header-actions">
        <div
          v-for="(action, index) in actions"
          :key="index"
          class="lockers__header-actions__action"
          :data-testid="'action' + index"
        >
          <img :src="require(`@/assets/icons/${action.value}.svg`)" alt="" />
          <p>{{ $t(`${action.label}`) }}</p>
        </div>
      </div>
    </div>
    <div v-if="activePickupBox?.connectionAlarmed" class="error__connection">
      <img src="@/assets/icons/Error.svg" alt="" />
      <div class="error__connection-messages">
        <div class="message">
          <p>{{ $t('connectionLost') }}</p>
        </div>
      </div>
    </div>
    <div
      :class="{
        lockers__modules: activePickupBox,
        lockers__notModules: !activePickupBox
      }"
    >
      <Modules
        :modules="modules"
        :active-pickup-box="activePickupBox"
        :multiple-lockers="multipleLockers"
        @multiple-lockers-false="multipleLockersFalse"
      />
    </div>
  </div>
  <QuickReservation
    v-if="showModalQuickReservation"
    :show-modal-quick-reservation="showModalQuickReservation"
    :active-pickup-box="activePickupBox"
    :lockers-active-box="lockersActiveBox"
    @close-modal="showModalQuickReservation = false"
  />
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import Loading from '@/views/Loading.vue';
import Modules from './Modules/Modules.vue';
import QuickReservation from './QuickReservation/QuickReservation.vue';

import { actions } from './configs';

import {
  LockerWithStatus,
  ModuleWithTemperature,
  PickupBox,
  Locker,
  LockersType,
  Module
} from '@/store/modules/pharmacy/types';

import { LangOptions, Option } from '@/types';
import { goToPickupBox } from '@/utils/goToPickupBox';

@Options({
  name: 'Lockers',
  components: {
    Loading,
    Modules,
    QuickReservation
  },
  data() {
    return {
      loading: false,
      actions,
      activePickupBox: null,
      modules: [],
      selectedPickupBox: {
        value: '',
        label: ''
      },
      pickupBoxOptions: [],
      lockersActiveBox: {},
      multipleLockers: false,
      showModalQuickReservation: false,
      disableButton: false,
      pickupBoxActiveId: null
    };
  },
  computed: {
    ...mapState('pharmacy', ['pharmacy', 'pickupBoxes']),
    ...mapState('account', ['user'])
  },
  created() {
    this.loadPickupBoxes();
  },
  methods: {
    ...mapActions('pharmacy', ['getPickupBoxesByPharmacy']),
    async loadPickupBoxes() {
      const { id, pickupBoxActiveId } = this.$route.params;
      this.pickupBoxActiveId = pickupBoxActiveId;

      this.loading = true;
      await this.getPickupBoxesByPharmacy(id);
      this.loading = false;

      this.initPickupBoxOptions();
    },
    initPickupBoxOptions() {
      const { boxId } = this.$route.params;

      this.pickupBoxOptions = this.pickupBoxes.map((i: PickupBox) => ({
        value: i.info.id,
        label: i.info.name,
        img: require('@/assets/icons/PickupBox.svg')
      }));

      this.selectedPickupBox = boxId
        ? this.pickupBoxOptions.find((boxOpt: LangOptions) => {
            return boxOpt.value === boxId;
          })
        : this.pickupBoxOptions[0];

      this.activePickupBox = boxId
        ? this.pickupBoxes.find((box: PickupBox) => {
            return box.id === boxId;
          })
        : this.pickupBoxes[0];

      this.modules = this.activePickupBox?.info.modules.map(
        (module: Module, index: number) => ({
          ...module,
          id: module.id ?? index
        })
      );

      this.initialTemperature();
      this.getFreeLockers();
    },
    // eslint-disable-next-line
    getTemperatureOfLockerFridge(module: any, lockerId: string) {
      if (module.fridgeTemperatures !== undefined) {
        return module.fridgeTemperatures[lockerId];
      }
    },
    initialTemperature() {
      this.modules = this.modules?.map(
        (module: ModuleWithTemperature, index: number) => {
          return {
            ...module,
            lockers: module.lockers.map((locker: LockerWithStatus) => {
              return {
                ...locker,
                temperature: locker.fridge
                  ? this.getTemperatureOfLockerFridge(
                      module,
                      `${index}-${locker.type}`
                    )
                  : module.temperature
              };
            })
          };
        }
      );
    },
    getVariant() {
      if (this.activePickupBox?.info.active) {
        if (!this.disableButton) {
          return 'filled';
        } else {
          return 'disabled';
        }
      } else {
        return 'disabled';
      }
    },
    changePickupBox(option: Option) {
      this.selectedPickupBox = option;
      this.activePickupBox = this.pickupBoxes.find(
        (i: PickupBox) => i.info.id === option.value
      );

      this.modules = this.activePickupBox.info.modules.map(
        (module: Module, index: number) => ({
          ...module,
          id: module.id ?? index
        })
      );

      goToPickupBox(this.user?.pharmacyId, this.activePickupBox.id);
      this.initialTemperature();
      this.getFreeLockers();
    },
    multipleLockersFalse() {
      this.multipleLockers = false;
    },
    getFreeLockers() {
      const allModulesLockers = this.activePickupBox?.info.modules
        .map((module: Module) => module.lockers)
        .flat();

      this.lockersActiveBox = allModulesLockers?.reduce(
        (lockers: LockersType, currentLocker: Locker) => {
          return {
            0:
              lockers[0] +
              +(
                currentLocker.type === 0 &&
                !currentLocker.order &&
                currentLocker.status !== 'inactive'
              ),
            1:
              lockers[1] +
              +(
                currentLocker.type === 1 &&
                !currentLocker.order &&
                !currentLocker.fridge &&
                currentLocker.status !== 'inactive'
              ),
            2:
              lockers[2] +
              +(
                currentLocker.type === 2 &&
                !currentLocker.order &&
                currentLocker.status !== 'inactive'
              ),
            fridge:
              lockers.fridge +
              +(
                currentLocker.fridge &&
                !currentLocker.order &&
                currentLocker.status !== 'inactive'
              )
          };
        },
        { 0: 0, 1: 0, 2: 0, fridge: 0 }
      );

      const valuesIsZero = (currentValue: unknown) => currentValue === 0;

      if (this.lockersActiveBox) {
        const valuesLockersActiveBox = Object.values(
          this.lockersActiveBox
        ).every(valuesIsZero);

        if (valuesLockersActiveBox) {
          return (this.disableButton = true);
        } else {
          return (this.disableButton = false);
        }
      }
    },
    quickReservation() {
      this.showModalQuickReservation = true;
    }
  },
  watch: {
    pickupBoxes() {
      this.multipleLockers = false;
      this.initPickupBoxOptions();
    }
  }
})
export default class Lockers extends Vue {}
</script>

<style lang="scss" scoped>
.lockers {
  .lockers__header {
    display: flex;
    justify-content: space-between;
    padding: 17px 24px;
    .lockers__header__inputs {
      display: flex;
      align-items: center;
      .lockers__header__inputs-dropdown {
        height: 40px;
        width: 288px !important;
      }
      .lockers__header-notPickupBox {
        margin-right: 12px;
      }
      .selectMultipleLockers {
        margin: 0 24px;
      }
      .tootip {
        position: relative;
        .tooltiptext {
          position: absolute;
          top: -30%;
          left: 50%;
          transform: translate(-50%, -100%);
          visibility: hidden;
          width: 150px;
          font-size: $elevenSize;
          line-height: $elevenSize;
          background: $label;
          color: $white;
          text-align: center;
          border-radius: 6px;
          padding: 5px;
          z-index: 1;
          opacity: 0;
          transition: opacity 0.3s;
          &::after {
            content: '';
            position: absolute;
            top: 99%;
            left: 50%;
            margin-left: -5px;
            border-width: 5px;
            border-style: solid;
            border-color: $label transparent transparent transparent;
          }
        }

        &:hover .tooltiptext {
          visibility: visible;
          opacity: 1;
        }
      }
      .lockers__header__quickReservation {
        .lockers__header__quickReservation-button {
          width: 150px;
        }
        .lockers__header__quickReservation-disabled {
          color: $main-text;
        }
      }
    }
    .lockers__header-actions {
      display: flex;
      .lockers__header-actions__action {
        display: flex;
        align-items: center;
        margin-right: 24px;
        &:last-child {
          margin-right: 0;
        }
        img {
          width: 12px;
        }
        p {
          font-size: $thirteenSize;
          margin-left: 8px;
        }
      }
    }
  }
  .error__connection {
    padding: 5px;
    margin: 0 24px;
    width: 175px;
    display: flex;
    align-items: center;
    column-gap: 6px;
    border: 1px solid $red;
    border-radius: 8px;
    font-size: $twelveSize;
    background: rgba(225, 82, 64, 0.06);
    img {
      background: rgba(225, 82, 64, -0.94);
    }
    .error__connection-messages {
      border-radius: 8px;
      background: rgba(225, 82, 64, -0.94);
      .message {
        background: rgba(225, 82, 64, -0.94);
        p {
          display: inline-block;
          color: $errorRed;
          background: rgba(225, 82, 64, -0.94);
        }
      }
    }
  }
  .lockers__modules {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-top: 5px;
  }
  .lockers__notModules {
    display: none;
    margin-top: 24px;
  }
}

@media screen and (max-width: 1350px) {
  .lockers__header {
    flex-direction: column;
    .lockers__header__inputs {
      margin-bottom: 24px;
    }
    .lockers__header-actions {
      justify-content: end;
    }
  }
}
</style>
